import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, of } from 'rxjs'
import { catchError, map, shareReplay, startWith } from 'rxjs/operators'
import { environment } from '@env/environment'
import { CustomerPortal } from '../models/customer.model'

export interface CustomerViewState {
  customer: CustomerPortal
  loading: boolean
  /**
   * O ente deste portal não existe: o BFF respondeu 400 (sem `x-tenant-id` válido) ou 404, ou
   * respondeu sem os dados da entidade. O `tenantAccessGuard` leva para a página de erro 404.
   */
  notFound: boolean
}

/** Respostas do `/customer` que significam "não há ente para este portal". */
const NOT_FOUND_STATUSES = [400, 404]

const fallbackCustomer: CustomerPortal = {
  id: '',
  name: 'Portal de Compras Públicas',
  document: '',
  email: null,
  contacts: {}
}

@Injectable({
  providedIn: 'root'
})
export class CustomerService {
  /**
   * Uma consulta por carregamento do app. `refCount: false` mantém o resultado mesmo quando o guard
   * (primeiro inscrito) termina antes dos componentes assinarem — sem isso a consulta, e o reCAPTCHA
   * dela, se repetiriam. Falha temporária (5xx, rede) mantém o cliente padrão e o portal abre.
   */
  private readonly customerState$: Observable<CustomerViewState> = this.http
    .get<CustomerPortal | null>(`${environment.urlApi}/customer`)
    .pipe(
      map((customer): CustomerViewState => hasEntityData(customer)
        ? { customer, loading: false, notFound: false }
        : { customer: fallbackCustomer, loading: false, notFound: true }),
      catchError((error: HttpErrorResponse) => of({
        customer: fallbackCustomer,
        loading: false,
        notFound: NOT_FOUND_STATUSES.includes(error.status)
      })),
      startWith({ customer: fallbackCustomer, loading: true, notFound: false }),
      shareReplay({ bufferSize: 1, refCount: false })
    )

  constructor(private readonly http: HttpClient) {}

  current(): Observable<CustomerPortal> {
    return this.customerState$.pipe(map(({ customer }) => customer))
  }

  state(): Observable<CustomerViewState> {
    return this.customerState$
  }

  /** Nome do ente só quando ele foi carregado; o cliente padrão (carregando ou em erro) não tem `id`. */
  entityName(): Observable<string | null> {
    return this.customerState$.pipe(
      map(({ customer, loading }) => (!loading && customer.id ? customer.name : null))
    )
  }
}

function hasEntityData(customer: CustomerPortal | null | undefined): customer is CustomerPortal {
  return !!customer?.id && !!customer.name?.trim()
}
